// ── 계약 관리 ⚙️ 설정 — 계약 등록 창에 보일 칸 정하기 ────────────────────────
// 여기서 저장하면 신규 계약 등록 / 검색 후 계약 등록 / 수정 / 재주문 네 경로가 모두 바뀐다.
// 저장 위치는 테넌트별 app_settings 한 줄 (api/services/tm/contract-field-config.js).
import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../../../components/Modal';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { FIELD_SECTIONS, UNITS, orderedSectionIds } from './contractFieldMeta';
import { SettingsFieldRow, Switch } from './ContractFieldSettingsRow';
import { ContractFieldPreview } from './ContractFieldPreview';
import { useContractApi } from './contractApi';

export function ContractFieldSettingsModal({ open, onClose, onSaved, ticketTypes = [] }) {
  const { showToast } = useToast();
  const api = useContractApi();
  const [config, setConfig] = useState(null);
  const [promoAvailable, setPromoAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    const r = await apiClient.get(api.fieldConfig);
    setLoading(false);
    if (!r.ok) { showToast('설정을 불러오지 못했습니다.', 'error'); return; }
    setConfig(r.data?.config || null);
    setPromoAvailable(Boolean(r.data?.promo_available));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 열 때마다 다시 읽는다 — 다른 관리자가 바꾼 값을 반영
  useEffect(() => { if (open) fetchConfig(); }, [open, fetchConfig]);

  function patchSection(id, patch) {
    setConfig((p) => ({ ...p, sections: { ...p.sections, [id]: { ...p.sections[id], ...patch } } }));
  }
  function patchField(id, patch) {
    setConfig((p) => ({ ...p, fields: { ...p.fields, [id]: { ...p.fields[id], ...patch } } }));
  }

  // 섹션 차례 바꾸기 — 이웃과 order 를 맞바꾼다
  function moveSection(id, dir) {
    setConfig((p) => {
      const ids = orderedSectionIds(p);
      const i = ids.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ids.length) return p;
      const next = { ...p, sections: { ...p.sections } };
      ids.forEach((sid, idx) => { next.sections[sid] = { ...next.sections[sid], order: idx }; });
      next.sections[ids[i]] = { ...next.sections[ids[i]], order: j };
      next.sections[ids[j]] = { ...next.sections[ids[j]], order: i };
      return next;
    });
  }

  async function save() {
    setSaving(true);
    const r = await apiClient.put(api.fieldConfigSave, { config });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '저장에 실패했습니다.', 'error'); return; }
    showToast('계약 등록 창 설정을 저장했습니다.', 'success');
    onSaved?.();
    onClose?.();
  }

  const footer = (
    <div className="flex justify-end gap-2">
      <button
        type="button" onClick={onClose}
        className="h-9 rounded-lg border border-[var(--border)] bg-[var(--bg-subtle)] px-4 text-[12px] font-bold text-[var(--text-sub)]"
      >
        취소
      </button>
      <button
        type="button" onClick={save} disabled={saving || loading || !config}
        className="h-9 rounded-lg bg-slate-900 dark:bg-accent-600 dark:hover:bg-accent-500 px-4 text-[12px] font-black text-white disabled:opacity-50"
      >
        {saving ? '저장 중...' : '저장'}
      </button>
    </div>
  );

  return (
    <Modal
      open={open} onClose={onClose} footer={footer}
      title="계약 등록 창 설정" icon="⚙️"
      width="min(1100px, calc(100vw - 48px))"
      mobileSheet mobileSheetDragClose
    >
      <div className="flex flex-col gap-3">
        <div className="text-[11px] leading-relaxed text-[var(--text-muted)]">
          계약을 등록하거나 수정할 때 뜨는 창에 <strong>어떤 칸을 보일지</strong> 정합니다.
          여기서 저장하면 <strong>신규 계약 등록</strong>과 <strong>검색 후 계약 등록</strong> 모두 똑같이 바뀝니다.
        </div>

        {loading && <div className="py-6 text-center text-[12px] text-[var(--text-muted)]">불러오는 중…</div>}

        {!loading && config && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] lg:items-start">
        <div className="flex flex-col gap-3">
        {orderedSectionIds(config).map((sid, idx, arr) => {
          const sec = FIELD_SECTIONS.find((x) => x.id === sid);
          const sc = config.sections[sec.id] || {};
          const blocked = sec.needsModule && !promoAvailable;
          const onCount = sec.fields.filter((f) => config.fields[f.id]?.on).length;
          return (
            <div
              key={sec.id}
              className={`rounded-xl border border-[var(--border)] overflow-hidden ${sc.on && !blocked ? '' : 'opacity-60'}`}
            >
              <div className="flex items-center gap-2 border-b border-[var(--border)] bg-[var(--bg-subtle)] px-3 py-2">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: sec.color }} />
                <span className="flex-1 text-[13px] font-bold text-[var(--text)]">{sec.name}</span>
                <span className="text-[11px] tabular-nums text-[var(--text-muted)]">{onCount} / {sec.fields.length}</span>
                <Switch
                  id={`sec-${sec.id}`} checked={Boolean(sc.on) && !blocked} disabled={blocked}
                  label={`${sec.name} 보이기`}
                  onChange={(v) => patchSection(sec.id, { on: v })}
                />
                <span className="inline-flex gap-[2px]">
                  {[['↑', -1, idx === 0], ['↓', 1, idx === arr.length - 1]].map(([sym, dir, off]) => (
                    <button
                      key={sym} type="button" disabled={off}
                      aria-label={`${sec.name} ${dir < 0 ? '위로' : '아래로'}`}
                      onClick={() => moveSection(sec.id, dir)}
                      className="h-5 w-5 rounded-md border border-[var(--border)] bg-[var(--bg-card)] text-[11px] leading-none text-[var(--text-sub)] disabled:opacity-30"
                    >
                      {sym}
                    </button>
                  ))}
                </span>
              </div>

              {blocked && (
                <div className="px-3 py-2 text-[11px] leading-relaxed text-[var(--text-muted)]">
                  이 회사는 <strong>홍보물 주문</strong> 기능을 아직 받지 않았습니다.
                  운영자가 기능을 넣어 주면 그때부터 켤 수 있습니다.
                </div>
              )}

              {!blocked && sc.on && (
                <div className="flex flex-col gap-1.5 p-2">
                  {sec.fields.map((f) => (
                    <SettingsFieldRow
                      key={f.id} meta={f} value={config.fields[f.id] || {}}
                      units={UNITS} onChange={(patch) => patchField(f.id, patch)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        </div>

        {/* 오른쪽 — 실제 창 미리보기 (여기서 누르는 건 저장되지 않습니다) */}
        <div className="flex flex-col gap-2 lg:sticky lg:top-0">
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-bold text-[var(--text)]">미리보기</span>
            <span className="text-[10.5px] text-[var(--text-muted)]">계약 등록 창</span>
          </div>
          <ContractFieldPreview config={config} ticketTypes={ticketTypes} />
          <div className="text-[10.5px] leading-relaxed text-[var(--text-muted)]">
            실제 창과 같은 화면입니다. 여기서 입력하거나 눌러도 저장되지 않습니다.
          </div>
        </div>
        </div>
        )}
      </div>
    </Modal>
  );
}
